import { useEffect, useRef, useState, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { ChevronDown, ChevronUp } from "lucide-react"

interface Props {
    children: ReactNode
    collapsedMaxHeight?: number
    className?: string
}

export const CollapsibleText = ({ children, collapsedMaxHeight = 140, className = "" }: Props) => {
    const { t } = useTranslation()
    const ref = useRef<HTMLDivElement>(null)
    const [overflows, setOverflows] = useState(false)
    const [expanded, setExpanded] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        const measure = () => setOverflows(el.scrollHeight > collapsedMaxHeight + 4)
        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(el)
        return () => ro.disconnect()
    }, [collapsedMaxHeight, children])

    const showToggle = overflows
    const isCollapsed = showToggle && !expanded

    return (
        <div className={className}>
            <div className="relative">
                <div
                    ref={ref}
                    style={isCollapsed ? { maxHeight: collapsedMaxHeight, overflow: "hidden" } : undefined}
                >
                    {children}
                </div>
                {isCollapsed && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
                )}
            </div>
            {showToggle && (
                <button
                    type="button"
                    onClick={() => setExpanded(v => !v)}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-black text-brand hover:text-brand-dark transition-colors"
                >
                    {expanded ? t("guest.campDetail.readLess") : t("guest.campDetail.readMore")}
                    {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                </button>
            )}
        </div>
    )
}
